/* global SideDrawer, theme */

if (!customElements.get('cross-sell-drawer')) {
  class CrossSellDrawer extends SideDrawer {
    constructor() {
      super();
      this.updateTabNav = this.updateTabNav.bind(this);
      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleDrawerClick = this.handleDrawerClick.bind(this);
      this.handleTabKeydown = this.handleTabKeydown.bind(this);
      this.handleExternalOpen = this.handleExternalOpen.bind(this);
      this.rtl = document.dir === 'rtl';
      this.recommendationsRequestId = 0;
      this.refreshElements();
      this.bindEvents();
    }

    bindEvents() {
      this.addEventListener('click', this.handleDrawerClick);
      this.addEventListener('keydown', this.handleTabKeydown);
      document.addEventListener('dispatch:cross-sell-drawer:open', this.handleExternalOpen);

      this.bindTabNav();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleDrawerClick);
      this.removeEventListener('keydown', this.handleTabKeydown);
      document.removeEventListener('dispatch:cross-sell-drawer:open', this.handleExternalOpen);
      this.unbindTabNav();
      this.abortRecommendationsLoad();
    }

    handleExternalOpen(evt) {
      this.open(evt.detail?.opener ?? null);
    }

    refreshElements() {
      this.tabs = Array.from(this.querySelectorAll('[data-cross-sell-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-cross-sell-panel]'));
      this.productsContainer = this.querySelector('[data-cross-sell-drawer-products]');
      this.tabsContainer = this.querySelector('.cross-sell-drawer__tabs');
      this.tabsTrack = this.querySelector('.cross-sell-drawer__tabs-track');
      this.prevBtn = this.querySelector('.cross-sell-drawer__tabs-nav--prev');
      this.nextBtn = this.querySelector('.cross-sell-drawer__tabs-nav--next');
      this.tabs.forEach((tab) => {
        tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
      });
    }

    bindTabNav() {
      if (!this.tabsContainer || !this.prevBtn || !this.nextBtn) return;
      if (this.tabNavBound) return;

      const scrollEvent = 'onscrollend' in window ? 'scrollend' : 'scroll';
      this.tabsContainer.addEventListener(scrollEvent, this.updateTabNav, { passive: true });
      window.addEventListener('on:debounced-resize', this.updateTabNav, { passive: true });

      this.prevBtn.addEventListener('click', this.handlePrevClick);
      this.nextBtn.addEventListener('click', this.handleNextClick);
      this.tabNavBound = true;

      requestAnimationFrame(this.updateTabNav);
    }

    unbindTabNav() {
      if (!this.tabNavBound) return;

      const scrollEvent = 'onscrollend' in window ? 'scrollend' : 'scroll';
      this.tabsContainer?.removeEventListener(scrollEvent, this.updateTabNav);
      window.removeEventListener('on:debounced-resize', this.updateTabNav);
      this.prevBtn?.removeEventListener('click', this.handlePrevClick);
      this.nextBtn?.removeEventListener('click', this.handleNextClick);
      this.tabNavBound = false;
    }

    handlePrevClick() {
      this.scrollTabNav('previous');
    }

    handleNextClick() {
      this.scrollTabNav('next');
    }

    scrollTabNav(direction) {
      if (!this.tabsContainer) return;

      const containerRect = this.tabsContainer.getBoundingClientRect();
      const tolerance = 2;
      const isPrevious = direction === 'previous';
      const lookToLeft = this.rtl ? !isPrevious : isPrevious;
      const tabs = lookToLeft ? [...this.tabs].reverse() : this.tabs;
      const targetTab = tabs.find((tab) => {
        const tabRect = tab.getBoundingClientRect();
        return lookToLeft
          ? tabRect.left < containerRect.left - tolerance
          : tabRect.right > containerRect.right + tolerance;
      });

      if (!targetTab) return;

      targetTab.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }

    handleDrawerClick(evt) {
      const tab = evt.target.closest('[data-cross-sell-tab]');
      if (tab) {
        this.selectTab(tab.dataset.crossSellTab);
        return;
      }

      const addButton = evt.target.closest('.js-cross-sell-add');
      if (addButton) {
        this.addItem(addButton);
        return;
      }

    }

    handleTabKeydown(evt) {
      const tab = evt.target.closest('[data-cross-sell-tab]');
      if (!tab || !this.tabs.includes(tab)) return;

      const key = evt.key;
      const isPreviousKey = key === (this.rtl ? 'ArrowRight' : 'ArrowLeft');
      const isNextKey = key === (this.rtl ? 'ArrowLeft' : 'ArrowRight');
      if (!isPreviousKey && !isNextKey && key !== 'Home' && key !== 'End') return;

      evt.preventDefault();

      const currentIndex = this.tabs.indexOf(tab);
      let nextIndex = currentIndex;
      if (key === 'Home') nextIndex = 0;
      if (key === 'End') nextIndex = this.tabs.length - 1;
      if (isPreviousKey) nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (isNextKey) nextIndex = (currentIndex + 1) % this.tabs.length;

      const nextTab = this.tabs[nextIndex];
      this.selectTab(nextTab.dataset.crossSellTab);
      nextTab.focus();
    }

    selectTab(tabName) {
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.crossSellTab === tabName;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.crossSellPanel === tabName;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      const activeTab = this.tabs.find((tab) => tab.dataset.crossSellTab === tabName);
      if (activeTab) {
        activeTab.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }

    hasRecommendations() {
      return !!this.querySelector('.cross-sell-card');
    }

    abortRecommendationsLoad() {
      this.recommendationsAbortController?.abort();
      this.recommendationsAbortController = null;
      this.recommendationsRequestId += 1;
    }

    async loadRecommendations(variantId) {
      const currentVariantId = String(variantId || this.dataset.currentVariantId || '');
      if (this.dataset.loadedVariantId === currentVariantId) return this.hasRecommendations();
      if (!this.productsContainer || !this.dataset.contentSectionId) return false;
      this.abortRecommendationsLoad();
      this.recommendationsAbortController = new AbortController();
      const requestId = this.recommendationsRequestId;

      let url;
      if (currentVariantId) {
        const cartPath = new URL(theme.routes.cart, window.location.origin).pathname;
        const rootPath = cartPath.replace(/\/cart\/?$/, '/') || '/';
        const rootUrl = rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
        url = new URL(`${rootUrl}variants/${currentVariantId}/`, window.location.origin);
        url.searchParams.set('section_id', this.dataset.contentSectionId);
      } else {
        if (!this.dataset.productUrl) return false;
        url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.contentSectionId);
      }

      try {
        const response = await fetch(url.toString(), {
          signal: this.recommendationsAbortController.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        if (!response.ok) throw new Error(response.status);

        const html = await response.text();
        if (requestId !== this.recommendationsRequestId) return false;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-cross-sell-drawer-content]');
        if (!content) return false;

        const drawerContent = Array.from(content.children).find((child) => child.classList.contains('drawer__content'));
        this.unbindTabNav();
        this.productsContainer.innerHTML = drawerContent ? drawerContent.innerHTML : content.innerHTML;
        this.dataset.loadedVariantId = currentVariantId;
        this.refreshElements();
        this.bindTabNav();
        return this.hasRecommendations();
      } catch (error) {
        if (error.name === 'AbortError') return false;
        console.log(error); // eslint-disable-line
        return false;
      } finally {
        if (requestId === this.recommendationsRequestId) {
          this.recommendationsAbortController = null;
        }
      }
    }

    openIfReady(opener) {
      this.open(opener);
      requestAnimationFrame(this.updateTabNav);
      return true;
    }

    updateTabNav() {
      if (!this.tabsContainer || !this.prevBtn || !this.nextBtn) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.tabsContainer;
      const tolerance = 3;
      const maxScroll = Math.max(0, scrollWidth - clientWidth);
      const hasOverflow = maxScroll > tolerance;
      const scrollPosition = Math.min(maxScroll, Math.max(0, Math.abs(scrollLeft)));
      const atStart = !hasOverflow || scrollPosition <= tolerance;
      const atEnd = !hasOverflow || maxScroll - scrollPosition <= tolerance;
      const hideNavButtonsOnMobile = window.matchMedia('(max-width: 767px)').matches;

      this.prevBtn.hidden = !hasOverflow || hideNavButtonsOnMobile;
      this.nextBtn.hidden = !hasOverflow || hideNavButtonsOnMobile;
      this.prevBtn.disabled = atStart;
      this.nextBtn.disabled = atEnd;

      if (this.tabsTrack) {
        this.tabsTrack.classList.toggle('has-fade-start', !atStart);
        this.tabsTrack.classList.toggle('has-fade-end', !atEnd);
      }
    }

    async addItem(button) {
      if (button.getAttribute('aria-disabled') === 'true') return;

      const variantId = button.dataset.variantId;
      const originalText = button.dataset.addToCartText || button.textContent;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('sections', 'cart-icon-bubble');
        formData.append('sections_url', window.location.pathname);

        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
        const data = await response.json();

        if (!response.ok) throw new Error(data.description || data.message || response.status);

        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && data.sections && data.sections['cart-icon-bubble']) {
          cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        const card = button.closest('[data-cross-sell-product-id]');
        if (card) card.classList.add('is-added');
        button.textContent = theme.strings.addedToCart;

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      } catch (error) {
        console.log(error); // eslint-disable-line
        button.textContent = theme.strings.cartError || originalText;
      } finally {
        setTimeout(() => {
          button.classList.remove('is-loading');
          if (button.closest('.is-added')) return;
          button.removeAttribute('aria-disabled');
          button.textContent = originalText;
        }, 1400);
      }
    }
  }

  customElements.define('cross-sell-drawer', CrossSellDrawer);
}
